import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { toastApiError } from '../../../utils/errorMessage';

import {
  deviceChangeRequestApi,
  type DeviceChangeRequest,
  DeviceChangeRequestStatus,
  REASON_TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
} from 'src/api/deviceChangeRequest.api';

interface DeviceChangeRequestTabProps {
  employeeId: string;
  onDeviceReset?: () => void;
}

export default function DeviceChangeRequestTab({
  employeeId,
  onDeviceReset,
}: DeviceChangeRequestTabProps) {
  const [requests, setRequests] = useState<DeviceChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DeviceChangeRequest | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [status, setStatus] = useState<DeviceChangeRequestStatus>(
    DeviceChangeRequestStatus.APPROVED
  );
  const [notes, setNotes] = useState('');

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await deviceChangeRequestApi.getEmployeeRequests(employeeId);
      setRequests(data);
    } catch (error: any) {
      toast.error('Cihaz değişiklik talepleri yüklenirken hata oluştu');
      console.error('Error loading device change requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [employeeId]);

  const handleReview = (request: DeviceChangeRequest) => {
    setSelectedRequest(request);
    setStatus(DeviceChangeRequestStatus.APPROVED);
    setNotes('');
    setDialogOpen(true);
  };

  const handleConfirmReview = async () => {
    if (!selectedRequest) return;

    try {
      setReviewing(true);
      await deviceChangeRequestApi.reviewRequest(selectedRequest.id, {
        status,
        notes: notes.trim() || undefined,
      });
      toast.success(
        status === DeviceChangeRequestStatus.APPROVED
          ? 'Talep onaylandı ve cihaz kaydı sıfırlandı'
          : 'Talep reddedildi'
      );
      setDialogOpen(false);
      loadRequests();
      if (status === DeviceChangeRequestStatus.APPROVED && onDeviceReset) {
        onDeviceReset();
      }
    } catch (error: any) {
      toastApiError(error, 'Talep işlenirken hata oluştu');
    } finally {
      setReviewing(false);
    }
  };

  const getStatusIcon = (reqStatus: DeviceChangeRequestStatus) => {
    switch (reqStatus) {
      case DeviceChangeRequestStatus.APPROVED:
        return <CheckCircleIcon fontSize="small" />;
      case DeviceChangeRequestStatus.REJECTED:
        return <CancelIcon fontSize="small" />;
      default:
        return <AccessTimeIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Cihaz Değişiklik Talepleri</Typography>
        <Button variant="outlined" onClick={loadRequests}>
          Yenile
        </Button>
      </Stack>

      {requests.length === 0 ? (
        <Alert severity="info">Bu çalışan için cihaz değişiklik talebi bulunmuyor.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Sebep</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>Eski Cihaz ID</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    {format(new Date(request.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                  </TableCell>
                  <TableCell>{REASON_TYPE_LABELS[request.reasonType]}</TableCell>
                  <TableCell>
                    {request.reasonText ? (
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={request.reasonText}
                      >
                        {request.reasonText}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {request.oldDeviceId ? (
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        sx={{
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                        title={request.oldDeviceId}
                      >
                        {request.oldDeviceId}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(request.status)}
                      label={STATUS_LABELS[request.status]}
                      color={STATUS_COLORS[request.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {request.status === DeviceChangeRequestStatus.PENDING && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleReview(request)}
                      >
                        İncele
                      </Button>
                    )}
                    {request.status !== DeviceChangeRequestStatus.PENDING &&
                      request.reviewedByName && (
                        <Typography variant="caption" color="text.secondary">
                          {request.reviewedByName}
                        </Typography>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={dialogOpen} onClose={() => !reviewing && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cihaz Değişiklik Talebini İncele</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sebep
                </Typography>
                <Typography>{REASON_TYPE_LABELS[selectedRequest.reasonType]}</Typography>
              </Box>

              {selectedRequest.reasonText && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Açıklama
                  </Typography>
                  <Typography>{selectedRequest.reasonText}</Typography>
                </Box>
              )}

              {selectedRequest.oldDeviceId && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Eski Cihaz ID
                  </Typography>
                  <Typography fontFamily="monospace">{selectedRequest.oldDeviceId}</Typography>
                </Box>
              )}

              <FormControl fullWidth>
                <InputLabel>Karar</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DeviceChangeRequestStatus)}
                  label="Karar"
                >
                  <MenuItem value={DeviceChangeRequestStatus.APPROVED}>
                    Onayla (Cihaz kaydı sıfırlanacak)
                  </MenuItem>
                  <MenuItem value={DeviceChangeRequestStatus.REJECTED}>Reddet</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Notlar (İsteğe Bağlı)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
              />

              {status === DeviceChangeRequestStatus.APPROVED && (
                <Alert severity="info">
                  Onaylandığında çalışanın mevcut cihaz kaydı sıfırlanacak ve yeni bir cihaz
                  kaydedebilecektir.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={reviewing}>
            İptal
          </Button>
          <Button
            onClick={handleConfirmReview}
            variant="contained"
            color={status === DeviceChangeRequestStatus.APPROVED ? 'primary' : 'error'}
            disabled={reviewing}
          >
            {reviewing ? 'İşleniyor...' : status === DeviceChangeRequestStatus.APPROVED ? 'Onayla' : 'Reddet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
